/**
 * WP-24: the way in. Everything a new person does between signing in and watching agents work, and everything a
 * returning person does to start the next run: pick or create a repo, connect Claude Code with one command, write
 * or paste a spec, plan it, approve it (the approvals strip does that), and put each package on their own Claude
 * Code.
 *
 * Views here are pure and escape every value. They never contain what the person typed (the spec, a repo name):
 * the page keeps that in the field itself, so a re-render cannot eat it. No step asks for a key, token or secret.
 */
import { showConnectCommand } from "@botallion/ui";
import { html, type Html } from "./html.ts";
import { empty } from "./views/common.ts";

export interface RepoRow {
  org: string;
  name: string;
  clone: string;
  createdAt?: string;
}

/** What the page knows about the account's repos. `null` means not loaded yet. */
export interface HomeState {
  orgs: string[];
  repos: RepoRow[] | null;
  error: string | null;
  creating: boolean;
}

export interface PlanSummary {
  specId: string;
  packages: number;
  criteria: number;
  preSequenced: number;
  runtime: number;
  blocking: number;
  questions: number;
}

export interface AgentOffer {
  state: "busy" | "ready" | "error";
  prompt?: string;
  clone?: string;
  agentName?: string;
  error?: string;
}

export interface StartState {
  org: string;
  repo: string;
  /** Claude Code is connected: null until the account's connections have loaded. */
  connected: boolean | null;
  planning: boolean;
  /** The last plan made from the editor in this visit. */
  summary: PlanSummary | null;
  error: string | null;
  /** The starter spec was loaded into the editor and is unchanged. */
  starter: boolean;
  /** Whether the repo already has a plan: the editor then sits behind "New run". */
  hasPlan: boolean;
  newRunOpen: boolean;
  /** Packages whose fork is ready and which no agent has joined yet. */
  ready: { specId: string; wpId: string; title: string }[];
  offers: Record<string, AgentOffer>;
  /** The app's origin and the CLI package name, for the connect command. */
  command: string;
}

/** The one command that connects Claude Code and Git. */
export function connectCommand(org: string, repo: string): string {
  return `npx botallion connect ${org}/${repo}`;
}

function copyBox(text: string, label = "Copy"): Html {
  return html`<div class="copybox"><code>${text}</code><button type="button" data-action="copy-text" data-copy="${text}">${label}</button></div>`;
}

export function signedOutView(): Html {
  return html`<div class="start-card"><h2>Sign in to start</h2>
    <p>Botallion plans your spec, lets your agents claim their ground, and checks every push. Sign in with GitHub or an email link.</p>
    <p><a class="btn primary" id="start-signin" href="/sign-in">Sign in</a></p></div>`;
}

/** WP-25: the import Worker's page for an org (served on this host under /import). No org lets it pick the first. */
export function importHref(org: string): string {
  return org ? `/import?org=${encodeURIComponent(org)}` : "/import";
}

/** Signed in, no repo chosen: the repos they have, and the ways to make or bring one. */
export function homeView(h: HomeState): Html {
  const list =
    h.repos === null
      ? html`<p class="empty">Loading your repos...</p>`
      : h.repos.length
        ? html`<ul class="repo-list">${h.repos.map((r) => html`<li><a class="repo-link" href="?mode=live&amp;org=${r.org}&amp;repo=${r.name}"><strong>${r.name}</strong><span class="muted"> ${r.org}</span></a></li>`)}</ul>`
        : empty("No repos yet. Start from the demo app below, create an empty one, or import one from GitHub.");
  const multiOrg = h.orgs.length > 1;
  return html`<div class="start-card"><h2>${h.repos && h.repos.length ? "Pick a repo" : "Create your first repo"}</h2>
    ${h.error ? html`<p class="problem" role="alert">${h.error}</p>` : ""}
    ${list}
    <div class="create">
      ${multiOrg ? html`<label class="field">Org <select id="new-org">${h.orgs.map((o) => html`<option value="${o}">${o}</option>`)}</select></label>` : ""}
      <label class="field">Repo name <input id="new-repo-name" type="text" autocomplete="off" spellcheck="false" autocapitalize="none" placeholder="ladder" size="18" /></label>
      <div class="actions">
        <button type="button" class="btn primary" data-action="create-repo" data-template="demo"${h.creating ? " disabled" : ""}>${h.creating ? "Creating..." : "Start from the demo app"}</button>
        <button type="button" class="btn" data-action="create-repo" data-template=""${h.creating ? " disabled" : ""}>Create an empty repo</button>
        <a class="btn" href="${importHref(h.orgs[0] ?? "")}" data-action="import-repo">Import from GitHub</a>
      </div>
      <p class="muted">Letters, digits, dots, underscores and single hyphens. The demo app is a small ladder API and web page, ready for the starter spec. Import brings every branch and tag across from GitHub.</p>
    </div></div>`;
}

function offerRow(s: StartState, p: StartState["ready"][number]): Html {
  const key = `${p.specId}/${p.wpId}`;
  const o = s.offers[key];
  return html`<li class="offer">
    <div class="row"><strong>${p.wpId}</strong> <span>${p.title}</span>
      ${!o || o.state === "error" ? html`<button type="button" class="btn" data-action="offer-agent" data-spec="${p.specId}" data-wp="${p.wpId}">Start with Claude Code</button>` : ""}
      ${o?.state === "busy" ? html`<span class="muted">Getting it ready...</span>` : ""}</div>
    ${o?.state === "error" ? html`<p class="problem" role="alert">${o.error}</p>` : ""}
    ${o?.state === "ready" && o.prompt
      ? html`<ol class="how">
          <li>Open Claude Code in any folder and run <code>/mcp</code>. Choose <strong>botallion</strong>, then <strong>Authenticate</strong>, and approve <strong>${o.agentName}</strong> on ${p.wpId}.</li>
          <li>Paste this into Claude Code:${copyBox(o.prompt)}</li>
        </ol>`
      : ""}
  </li>`;
}

/** The repo's start panel: connect, write the spec, then start agents. */
export function startView(s: StartState): Html {
  const connect =
    s.connected === true
      ? html`<div class="step done"><h3><span class="tick">Done</span> 1. Claude Code is connected</h3>
          <p class="muted">Botallion is in Claude Code and Git is set up for this account. Manage it in <a href="/settings">Settings</a>.</p></div>`
      : showConnectCommand
        ? html`<div class="step"><h3>1. Connect Claude Code</h3>
          <p>Run this once in a terminal. It signs you in, sets up Git and adds Botallion to Claude Code. There is no key to copy.</p>
          ${copyBox(s.command)}
          <p class="muted">Needs Node 20 or later and Claude Code installed.</p></div>`
        : html`<div class="step"><h3>1. Connect Claude Code</h3>
          <p>Claude Code shows as connected here once it joins.</p></div>`;

  const editor = html`<div class="step" id="spec-step"><h3>${s.hasPlan ? "New run" : "2. Write the spec"}</h3>
      <p>${s.starter ? "This is the starter spec for the demo app. Keep it, edit it, or replace it with your own." : "Say what you want built, in plain language. Use one ## heading per feature and a ### Acceptance list under each."}</p>
      <textarea id="spec" rows="12" spellcheck="false" aria-label="Spec" placeholder="# Title&#10;&#10;## A feature&#10;What it should do.&#10;&#10;### Acceptance&#10;- How you will know it works"></textarea>
      <div class="actions"><button type="button" class="btn primary" data-action="plan-it"${s.planning ? " disabled" : ""}>${s.planning ? "Planning..." : "Plan it"}</button>
        <span class="muted">Planning takes a few seconds. Nothing starts until you approve.</span></div>
      ${s.error ? html`<p class="problem" role="alert">${s.error}</p>` : ""}
      ${s.summary
        ? html`<p class="notice" role="status">Planned ${s.summary.packages} ${s.summary.packages === 1 ? "package" : "packages"} with ${s.summary.criteria} acceptance ${s.summary.criteria === 1 ? "criterion" : "criteria"}.
            ${s.summary.preSequenced ? `${s.summary.preSequenced} overlap${s.summary.preSequenced === 1 ? " was" : "s were"} sequenced up front. ` : ""}${s.summary.runtime ? `${s.summary.runtime} will be settled by the agents while they work. ` : ""}${s.summary.blocking ? `${s.summary.blocking} blocking ${s.summary.blocking === 1 ? "question needs" : "questions need"} an answer before you can approve. ` : ""}Review the plan below, then press <strong>Approve and start</strong>.</p>`
        : ""}
    </div>`;

  const agents = s.ready.length
    ? html`<div class="step"><h3>3. Start your agents</h3>
        <p>The plan is approved and each package has its own fork. Start them one at a time in your Claude Code. A package starts as soon as its agent joins.</p>
        <ul class="offers">${s.ready.map((p) => offerRow(s, p))}</ul></div>`
    : "";

  const wrap = s.hasPlan
    ? html`<details id="new-run"${s.newRunOpen ? " open" : ""}><summary>New run</summary>${connect}${editor}</details>${agents}`
    : html`${connect}${editor}${agents}`;
  return html`<div class="start-card"><div class="start-head"><h2>${s.org}/${s.repo}</h2></div>${wrap}</div>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Talking to the Worker. Every call is same-origin with the session cookie; none carries a credential.
// ---------------------------------------------------------------------------------------------------------------

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;
const real: Fetch = (input, init) => fetch(input, init);

/** What went wrong and the next step, from any response shape the Worker, planner or accounts can send. */
export async function problemFrom(res: Response, doing: string): Promise<string> {
  let message = "";
  try {
    const body = (await res.json()) as { error?: { message?: string } | string; message?: string };
    message = (typeof body.error === "object" ? body.error?.message : undefined) ?? body.message ?? (typeof body.error === "string" ? body.error : "") ?? "";
  } catch {
    // not JSON
  }
  const hint =
    res.status === 401 ? " Sign in again, then retry."
    : res.status === 403 ? " Reload the page and retry; if it keeps happening, check Settings."
    : res.status === 404 ? " Check the org and repo name."
    : res.status >= 500 ? " That's on our side. Try again in a minute."
    : "";
  return message ? `${doing}: ${message}${/[.!?]$/.test(message) ? "" : "."}${hint}` : `${doing} (HTTP ${res.status}).${hint}`;
}

const networkProblem = (doing: string) => `${doing}: couldn't reach Botallion. Check your connection and try again.`;

const jsonInit = (body: unknown): RequestInit => ({ method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export async function listRepos(orgs: string[], doFetch: Fetch = real): Promise<{ repos: RepoRow[]; error: string | null }> {
  const repos: RepoRow[] = [];
  let error: string | null = null;
  await Promise.all(
    orgs.map(async (org) => {
      try {
        const res = await doFetch(`/api/orgs/${encodeURIComponent(org)}/repos`, { credentials: "same-origin", cache: "no-store" });
        if (!res.ok) {
          error = await problemFrom(res, "Couldn't load your repos");
          return;
        }
        const body = (await res.json()) as { repos?: { name: string; clone: string; createdAt?: string }[] };
        for (const r of body.repos ?? []) repos.push({ org, name: r.name, clone: r.clone, ...(r.createdAt ? { createdAt: r.createdAt } : {}) });
      } catch {
        error = networkProblem("Couldn't load your repos");
      }
    }),
  );
  // Newest first, so the repo they were just working in is at the top.
  return { repos: repos.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "") || a.org.localeCompare(b.org) || a.name.localeCompare(b.name)), error };
}

export async function createRepo(org: string, name: string, template: string, doFetch: Fetch = real): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await doFetch(`/api/orgs/${encodeURIComponent(org)}/repos`, jsonInit({ name, ...(template ? { template } : {}) }));
    if (res.ok) return { ok: true };
    if (res.status === 409) return { ok: false, error: `You already have a repo called ${name}. Pick it from the list, or choose another name.` };
    return { ok: false, error: await problemFrom(res, "Couldn't create the repo") };
  } catch {
    return { ok: false, error: networkProblem("Couldn't create the repo") };
  }
}

/** Is Claude Code connected for this account? Null when it can't be told (the command is shown either way). */
export async function claudeConnected(doFetch: Fetch = real): Promise<boolean | null> {
  try {
    const res = await doFetch("/api/account/credentials", { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as { connections?: { revokedAt?: string | null; expiresAt?: string | number | null }[] };
    return (body.connections ?? []).some((c) => !c.revokedAt);
  } catch {
    return null;
  }
}

export async function loadStarterSpec(doFetch: Fetch = real): Promise<string | null> {
  try {
    const res = await doFetch("/starter-spec.md", { cache: "no-store" });
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  }
}

export async function planSpec(org: string, repo: string, spec: string, doFetch: Fetch = real): Promise<{ ok: true; summary: PlanSummary } | { ok: false; error: string }> {
  if (!spec.trim()) return { ok: false, error: "The spec is empty. Write what you want built, or paste a spec, then press Plan it." };
  try {
    const res = await doFetch(`/api/orgs/${encodeURIComponent(org)}/repos/${encodeURIComponent(repo)}/planner/plans`, jsonInit({ spec }));
    if (!res.ok) {
      const base = await problemFrom(res, "Couldn't plan that spec");
      return { ok: false, error: res.status === 400 ? `${base} Each feature needs its own ## heading, and the spec needs at least one.` : base };
    }
    const body = (await res.json()) as { result?: { report?: { specId: string; counts: { packages: number; criteria: number; preSequenced: number; runtime: number; questions: number; blocking: number } } } };
    const report = body.result?.report;
    if (!report) return { ok: false, error: "The planner answered, but not with a plan. Try again in a moment." };
    return { ok: true, summary: { specId: report.specId, ...report.counts } };
  } catch {
    return { ok: false, error: networkProblem("Couldn't plan that spec") };
  }
}

export async function offerAgent(org: string, repo: string, specId: string, wpId: string, doFetch: Fetch = real): Promise<AgentOffer> {
  try {
    const res = await doFetch(`/api/orgs/${encodeURIComponent(org)}/repos/${encodeURIComponent(repo)}/agents`, jsonInit({ specId, wpId }));
    if (!res.ok) return { state: "error", error: await problemFrom(res, `Couldn't get ${wpId} ready`) };
    const body = (await res.json()) as { result?: { prompt: string; clone: string; agentName: string } };
    if (!body.result) return { state: "error", error: `Couldn't get ${wpId} ready: no answer. Try again.` };
    return { state: "ready", ...body.result };
  } catch {
    return { state: "error", error: networkProblem(`Couldn't get ${wpId} ready`) };
  }
}

/** Which repo to open without being asked: the only one. With several, the list is one click. */
export function pickRepo(repos: RepoRow[]): RepoRow | null {
  return repos.length === 1 ? repos[0]! : null;
}
